import React, { Component } from 'react';
import { Table, Button, Icon, Segment, Header, Input, Pagination, Message, Confirm } from 'semantic-ui-react';

import withPagination from 'hocs/withPagination';
import withSearch from 'hocs/withSearch';
import { archived_clients } from 'resources/adminResources';
import connectResource from 'utils/connectResource';
import { getPageAllById } from 'utils/connectors';
import formattedDate from 'utils/formattedDate';
import Main from 'components/Main';

// -----------------------------------------------------
// Component Definition
// -----------------------------------------------------

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      confirmOpen: false,
      clientToUnarchive: null,
      unarchiving: null
    };
    this.handleUnarchive = this.handleUnarchive.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.confirmUnarchive = this.confirmUnarchive.bind(this);
    this.handleConfirmCancel = this.handleConfirmCancel.bind(this);
  }

  render() {
    const { data, loading, query, page, setPage, totalPages } = this.props;
    const { confirmOpen, clientToUnarchive, unarchiving } = this.state;
    const archivedClients = data.archived_clients || [];

    return (
      <Main className='detail-pane' overflow="scroll" padded>
        <Segment>
          <Header as='h3'>
            <Icon name='archive' />
            Archived Clients
          </Header>
          
          {/* Search Input */}
          <div style={{ marginBottom: '20px' }}>
            <Input
              icon='search'
              iconPosition='left'
              placeholder='Search by name, ID, or organization...'
              value={query || ''}
              onChange={this.handleSearchChange}
              style={{ width: '400px' }}
            />
          </div>

          {/* Results Count */}
          {archivedClients.length > 0 && (
            <div style={{ marginBottom: '10px', color: '#666' }}>
              Showing {archivedClients.length} archived client{archivedClients.length !== 1 ? 's' : ''}
              {query && ` matching "${query}"`}
            </div>
          )}

          {/* Table */}
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>ID</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Organization</Table.HeaderCell>
                <Table.HeaderCell>Archived By</Table.HeaderCell>
                <Table.HeaderCell>Archived Date</Table.HeaderCell>
                <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {loading && (
                <Table.Row>
                  <Table.Cell colSpan='6' textAlign='center'>
                    <Icon loading name='spinner' /> Loading...
                  </Table.Cell>
                </Table.Row>
              )}

              {!loading && archivedClients.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan='6' textAlign='center'>
                    <Message info>
                      <Message.Header>No archived clients found</Message.Header>
                      {query ? `No clients match the search term "${query}"` : 'There are currently no archived clients.'}
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}

              {!loading && archivedClients.map(client => (
                <Table.Row key={client.id}>
                  <Table.Cell>{client.id}</Table.Cell>
                  <Table.Cell>
                    <strong>{client.first_name} {client.last_name}</strong>
                    {client.middle_name && (
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Middle: {client.middle_name}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>{client.organization?.name || 'N/A'}</Table.Cell>
                  <Table.Cell>
                    {client.archive?.administrator && (
                      <div>
                        {client.archive.administrator.first_name} {client.archive.administrator.last_name}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>{formattedDate(client.archive?.created_at)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      onClick={() => this.handleUnarchive(client)}
                      loading={unarchiving === client.id}
                    >
                      <Icon name="undo" />
                      Unarchive
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Pagination
                activePage={page}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => setPage(activePage)}
                ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
                firstItem={{ content: <Icon name='angle double left' />, icon: true }}
                lastItem={{ content: <Icon name='angle double right' />, icon: true }}
                prevItem={{ content: <Icon name='angle left' />, icon: true }}
                nextItem={{ content: <Icon name='angle right' />, icon: true }}
              />
            </div>
          )}
        </Segment>

        {/* Confirmation Dialog */}
        <Confirm
          open={confirmOpen}
          header='Unarchive Client'
          content={
            clientToUnarchive ? 
            `Are you sure you want to unarchive ${clientToUnarchive.first_name} ${clientToUnarchive.last_name} (ID: ${clientToUnarchive.id})?` 
            : ''
          }
          confirmButton={{ content: 'Unarchive', color: 'green' }}
          cancelButton='Cancel'
          onConfirm={this.confirmUnarchive}
          onCancel={this.handleConfirmCancel}
        />
      </Main>
    );
  }

  handleSearchChange(e, { value }) {
    const { onSearch } = this.props;
    onSearch({ query: value });
  }

  handleUnarchive(client) {
    this.setState({
      confirmOpen: true,
      clientToUnarchive: client
    });
  }

  handleConfirmCancel() {
    this.setState({
      confirmOpen: false,
      clientToUnarchive: null
    });
  }

  async confirmUnarchive() {
    const { destroy, index } = this.props;
    const { clientToUnarchive } = this.state;
    
    if (!clientToUnarchive) return;
    
    // Close confirmation dialog and set loading state
    this.setState({ 
      confirmOpen: false,
      clientToUnarchive: null,
      unarchiving: clientToUnarchive.id 
    });
    
    try {
      await destroy(null, { id: clientToUnarchive.id });
      // Refresh the list by calling index action
      await index();
    } catch (error) {
      console.error('Error unarchiving client:', error);
      // You might want to show an error message here
    } finally {
      // Clear loading state
      this.setState({ unarchiving: null });
    }
  }
}

// --------------------------------------------------------
// Public API
// --------------------------------------------------------

const connectedComponent = connectResource(App, {
  key: ({ page, query }) => [page, query],
  connectors: {
    archived_clients: getPageAllById(archived_clients)
  },
  mapDispatchToProps: { ...archived_clients.actions }
});

export default withSearch(withPagination(connectedComponent)); 